// ============================================================================
// MarkdownBody — render UIF event description (and future Agenda/Presenters/
// About fields) from markdown source.
// ============================================================================
// Built on react-markdown + remark-gfm + rehype-raw:
//   * remark-gfm: GitHub-flavored markdown — tables, task lists, strikethrough,
//     autolinked URLs, footnotes.
//   * rehype-raw: allow raw HTML inside markdown. We need this so UIF can
//     paste <center>…</center> wrappers for visually centered callouts. The
//     descriptions are author-trusted (UIF role required to edit), so the
//     usual XSS concerns don't apply here — this is a CMS, not user-generated
//     content from the public web.
//
// Styling is applied via the `components` prop instead of a global CSS class,
// so the look stays in sync with the prior renderDescription output:
//   * paragraphs:  text-gray-700, mb-4, leading-relaxed
//   * bullets:     list-disc list-outside pl-6 mb-4 space-y-2
//   * headings:    inherits font-semibold via base style; we down-tune sizes
//   * blockquote:  italic centered "lead" feel for short emphasis lines
//   * links:       uif-green underlined
//   * code/strong/em: lightly themed
//
// Centered text from <center>…</center> falls through rehype-raw unchanged
// (the user agent renders it natively).
// ============================================================================

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface MarkdownBodyProps {
  source: string;
}

// Legacy Food Safety descriptions (and possibly other older content) use the
// unicode bullet character "•" as list markers. react-markdown only treats
// "-", "*", or "+" as bullets, so those lines rendered as flowing paragraph
// text. Preprocess by replacing leading "• " with "- " line-by-line.
function normalizeBullets(src: string): string {
  return src
    .split('\n')
    .map((line) => line.replace(/^(\s*)•\s+/, '$1- '))
    .join('\n');
}

export function MarkdownBody({ source }: MarkdownBodyProps) {
  const normalized = normalizeBullets(source);
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        p: ({ children }) => (
          <p className="mb-4 leading-relaxed text-gray-700">{children}</p>
        ),
        ul: ({ children }) => (
          <ul className="list-disc list-outside pl-6 mb-4 space-y-2 text-gray-700">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-outside pl-6 mb-4 space-y-2 text-gray-700">{children}</ol>
        ),
        li: ({ children }) => <li className="leading-relaxed">{children}</li>,
        h1: ({ children }) => (
          <h1 className="text-xl font-bold text-uif-green-dark mt-6 mb-3">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-lg font-bold text-uif-green-dark mt-5 mb-2">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-base font-semibold text-uif-green-dark mt-4 mb-2">{children}</h3>
        ),
        a: ({ href, children }) => (
          <a
            href={href}
            target={href?.startsWith('http') ? '_blank' : undefined}
            rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            className="text-uif-green underline hover:text-uif-green-dark"
          >
            {children}
          </a>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold text-gray-900">{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        blockquote: ({ children }) => (
          <blockquote className="mb-4 px-4 py-2 border-l-4 border-uif-green/40 text-gray-700 italic">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-6 border-gray-200" />,
        code: ({ children }) => (
          <code className="bg-gray-100 rounded px-1 py-0.5 text-xs font-mono text-uif-brown-dark">
            {children}
          </code>
        ),
        img: ({ src, alt }) => (
          // Headshots in presenters_md render as circular avatars that float
          // left so paragraphs of bio text wrap to the right. On mobile they
          // center-stack above the text.
          <img
            src={src}
            alt={alt || ''}
            loading="lazy"
            className="block sm:float-left sm:mr-5 sm:mb-2 mx-auto sm:mx-0 h-32 w-32 rounded-full object-cover ring-2 ring-uif-green/20 my-3"
          />
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-sm border border-gray-200">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="bg-gray-50 border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-900">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border-b border-gray-100 px-3 py-2 text-gray-700">{children}</td>
        ),
      }}
    >
      {normalized}
    </ReactMarkdown>
  );
}
